import React, { useEffect, useState, useMemo, useContext, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ProfileContext } from "../../../context/ProfileProvider";
import AuthService from "../../../api/services/AuthService";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import { AIRDROP_TASKS_SECTION_HASH } from "../../../customComponents/AirdropPromoBannerModal/index.js";
import "./airdrop.css";

const SOCIAL_DEFAULT_LABELS = {
  1: "Follow on X",
  2: "Open Telegram",
  3: "Follow on Instagram",
  4: "Subscribe on YouTube",
  5: "Join WhatsApp",
};
const SOCIAL_LINK_FALLBACK = {
  1: "https://x.com/Zillion_exc",
  2: "https://t.me/zillionexc",
  3: "https://www.instagram.com/zillion_exc?utm_source=qr&igsh=MXZsOHlxejgzbmppaQ==",
  4: "https://www.youtube.com/@ZillionExchange",
  5:"https://whatsapp.com/channel/0029VbCYwlUAe5VwyoR1OC30",
};

const normalizeSocialUrl = (v) => {
  if (v == null) return null;
  const s = String(v).trim();
  if (!s || s === "null" || s === "undefined") return null;
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  if (s.startsWith("www.")) return `https://${s}`;
  return null;
};

function getSocialUrlFromOtherSettings(settings, taskNum) {
  if (!settings) return null;
  const n = String(taskNum);
  const i = taskNum - 1;
  for (const name of ["socialLinks", "social_links", "airdropSocialLinks"]) {
    const arr = settings[name];
    if (Array.isArray(arr) && arr[i] != null) {
      const u = normalizeSocialUrl(arr[i]);
      if (u) return u;
    }
  }
  const keyCandidates = [
    `socialTask${n}Link`,
    `socialTask${n}`,
    `socialLink${n}`,
    `social_link_${n}`,
    `social_task_${n}_url`,
    `social_task_${n}_link`,
    `social_task_${n}`,
    `url${n}`,
    `link${n}`,
  ];
  for (const k of keyCandidates) {
    const u = normalizeSocialUrl(settings[k]);
    if (u) return u;
  }
  if (n === "1") {
    const u = normalizeSocialUrl(settings.twitterUrl) || normalizeSocialUrl(settings.xUrl);
    if (u) return u;
  }
  if (n === "2") {
    const u = normalizeSocialUrl(settings.telegramUrl) || normalizeSocialUrl(settings.telegram);
    if (u) return u;
  }
  if (n === "3") {
    const u = normalizeSocialUrl(settings.instagramUrl) || normalizeSocialUrl(settings.instagram);
    if (u) return u;
  }
  if (n === "4") {
    const u =
      normalizeSocialUrl(settings.youtubeUrl) ||
      normalizeSocialUrl(settings.youtubeChannelUrl) ||
      normalizeSocialUrl(settings.youtube);
    if (u) return u;
  }
  if (n === "5") {
    const u =
      normalizeSocialUrl(settings.whatsappUrl) ||
      normalizeSocialUrl(settings.whatsAppUrl) ||
      normalizeSocialUrl(settings.whatsapp);
    if (u) return u;
  }
  return null;
}

const formatRewardAmount = (value, currencyShortName) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const n = Number(value);
  const ccy = currencyShortName != null && String(currencyShortName).trim() !== "" ? ` ${String(currencyShortName).trim()}` : "";
  return `${n.toLocaleString()}${ccy}`;
};

const stepNumClass = ["airdrop_step_num--pink", "airdrop_step_num--blue", "airdrop_step_num--green"];
/** Guest view: mirrors referral signup tasks (link tasks + verified mobile). */
const GUEST_STEPS = [
  {
    n: 1,
    title: "Follow on X",
    body: "Open our official X link, follow the account, and stay on top of drops and announcements.",
  },
  {
    n: 2,
    title: "Join Telegram",
    body: "Join our Telegram channel for community updates and task reminders.",
  },
  {
    n: 3,
    title: "Follow on Instagram",
    body: "Follow Zillion on Instagram for campaign visuals and news.",
  },
  {
    n: 4,
    title: "Subscribe on YouTube",
    body: "Subscribe to our YouTube channel for tutorials and release updates.",
  },
  {
    n: 5,
    title: "Join WhatsApp",
    body: "Join our WhatsApp channel for quick alerts when new tasks go live.",
  },
  // {
  //   n: 6,
  //   title: "Add & verify mobile",
  //   body: "After you sign up, add a verified mobile number to your account (phone signups already satisfy this).",
  // },
];

/** Logged-in users: link tasks 1–5 via POST complete-referral-social-task only; mobile is task 6 (backend / profile). */
const SOCIAL_TASK_DEFS = [
  {
    n: 1,
    title: "Follow on X",
    progressLabel: "X",
    iconClass: "ri-twitter-x-fill",
    hint: 'Tap “Open X” below — opening the link and follow our official X account.',
  },
  {
    n: 2,
    title: "Join Telegram",
    progressLabel: "Telegram",
    iconClass: "ri-telegram-2-fill",
    hint: 'Tap “Open Telegram” below — opening the link and follow our official Telegram account.',
  },
  {
    n: 3,
    title: "Follow on Instagram",
    progressLabel: "Instagram",
    iconClass: "ri-instagram-line",
    hint: 'Tap “Open Instagram” below — opening the link and follow our official Instagram account.',
  },
  {
    n: 4,
    title: "Subscribe on YouTube",
    progressLabel: "YouTube",
    iconClass: "ri-youtube-fill",
    hint: 'Tap “Open YouTube” below — opening the link and subscribe our official YouTube channel.',
  },
  {
    n: 5,
    title: "Join WhatsApp",
    progressLabel: "WhatsApp",
    iconClass: "ri-whatsapp-fill",
    hint: 'Tap “Open WhatsApp” below — opening the link and join our official WhatsApp group.',
  },
];

const MOBILE_TASK_PROGRESS = {
  progressLabel: "Mobile",
  iconClass: "ri-smartphone-line",
};

const TASK_SIX_API_USER_MESSAGE =
  "The mobile step is completed automatically when your account has a verified phone number. Do not use the social-task API for task 6.";

function parseReferralSocialTaskErrorMessage(message) {
  const m = String(message || "");
  if (
    /\btask\s*#?\s*6\b/i.test(m) ||
    /\btasknumber\b.*\b6\b/i.test(m) ||
    (/task\s*6/i.test(m) && /invalid|not\s+allowed|cannot|do\s+not/i.test(m))
  ) {
    return TASK_SIX_API_USER_MESSAGE;
  }
  return m || "Could not update task. Try again.";
}

function formatClaimSignupRewardError(raw, socialTasksRequired, socialLinkTasksRequired) {
  const code = String(raw?.code ?? raw?.errorCode ?? raw?.error ?? "");
  const msg = String(raw?.message ?? "");
  const blob = `${code} ${msg}`;
  if (/KYC_NOT_APPROVED|KYC\s*NOT\s*APPROVED/i.test(blob)) {
    return "Referral signup reward no longer depends on KYC. Refresh this page and try again, or contact support if this message keeps appearing.";
  }
  if (/SOCIAL_INCOMPLETE/i.test(blob) || /social.*not\s*complete|complete.*social/i.test(msg)) {
    return `Finish all prerequisites: ${socialLinkTasksRequired} social link visits plus a verified mobile on your account (${socialTasksRequired} steps total).`;
  }
  return msg || "Could not claim reward. Please try again.";
}

const AirDrop = () => {
  const location = useLocation();
  const { referralRewardStatus, fetchReferralRewardStatus, userDetails } = useContext(ProfileContext) || {};
  const [isLoggedIn, setIsLoggedIn] = useState(() => !!localStorage.getItem("token"));
  const [taskSubmitting, setTaskSubmitting] = useState(null);
  const [claimSubmitting, setClaimSubmitting] = useState(false);
  const [otherSettings, setOtherSettings] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [rewardStatusLoading, setRewardStatusLoading] = useState(false);
  const heroBgUrl = `${process.env.PUBLIC_URL || ""}/images/airdrop-img/airdrophero_bg.jpg`;
  const heroLightBgUrl = `${process.env.PUBLIC_URL || ""}/images/airdrop-img/airdrophero_light_bg.jpg`;
  const bonusBgUrl = `${process.env.PUBLIC_URL || ""}/images/airdrop-img/bonusbg.png`;
  const releaseBgUrl = `${process.env.PUBLIC_URL || ""}/images/airdrop-img/releasebg.jpg`;
  const releaseBgLightUrl = `${process.env.PUBLIC_URL || ""}/images/airdrop-img/releasebg-light.jpg`;

  const ccy = otherSettings?.currencyShortName;
  const signupDisplay = useMemo(
    () => formatRewardAmount(otherSettings?.signupBonus, ccy),
    [otherSettings?.signupBonus, ccy]
  );
  const referralTotal = useMemo(() => {
    if (!otherSettings) return null;
    const a = otherSettings.level1Reward;
    const b = otherSettings.level2Reward;
    const c = otherSettings.level3Reward;
    if (a == null && b == null && c == null) return null;
    const s = (Number(a) || 0) + (Number(b) || 0) + (Number(c) || 0);
    return s;
  }, [otherSettings]);
  const referralTotalDisplay = useMemo(
    () => formatRewardAmount(referralTotal, ccy),
    [referralTotal, ccy]
  );
  const level1Display = useMemo(
    () => formatRewardAmount(otherSettings?.level1Reward, ccy),
    [otherSettings?.level1Reward, ccy]
  );
  const level2Display = useMemo(
    () => formatRewardAmount(otherSettings?.level2Reward, ccy),
    [otherSettings?.level2Reward, ccy]
  );
  const level3Display = useMemo(
    () => formatRewardAmount(otherSettings?.level3Reward, ccy),
    [otherSettings?.level3Reward, ccy]
  );

  const socialTasksRequired = useMemo(() => {
    const t = Number(referralRewardStatus?.socialTasksRequired ?? referralRewardStatus?.total);
    if (Number.isFinite(t) && t > 0) return t;
    return 6;
  }, [referralRewardStatus]);

  const socialLinkTasksRequired = useMemo(() => {
    const t = Number(referralRewardStatus?.socialLinkTasksRequired);
    if (Number.isFinite(t) && t > 0) return t;
    return 5;
  }, [referralRewardStatus]);

  const mobileTaskId = useMemo(() => {
    const t = Number(referralRewardStatus?.referralMobileTaskId);
    return Number.isFinite(t) && t > 0 ? t : 6;
  }, [referralRewardStatus]);

  const isPhoneSignupUser = userDetails?.registeredBy === "phone";

  const satisfiedTaskIds = useMemo(() => {
    const ids = new Set(
      (referralRewardStatus?.referral_social_tasks || [])
        .map(Number)
        .filter((x) => Number.isFinite(x) && x >= 1)
    );
    if (referralRewardStatus?.mobileTaskSatisfied) {
      ids.add(mobileTaskId);
    }
    return ids;
  }, [referralRewardStatus?.referral_social_tasks, referralRewardStatus?.mobileTaskSatisfied, mobileTaskId]);

  const isTaskComplete = (taskNum) => {
    if (referralRewardStatus?.rewardClaimed || referralRewardStatus?.socialTasksCompleted) return true;
    return satisfiedTaskIds.has(Number(taskNum));
  };

  const socialProgress = useMemo(() => {
    const total = SOCIAL_TASK_DEFS.length;
    let done;
    if (referralRewardStatus?.rewardClaimed || referralRewardStatus?.socialTasksCompleted) {
      done = total;
    } else {
      done = SOCIAL_TASK_DEFS.filter(({ n }) => satisfiedTaskIds.has(Number(n))).length;
    }
    done = Math.min(Math.max(done, 0), total);
    const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
    return { done, total, pct };
  }, [referralRewardStatus, satisfiedTaskIds]);

  const getLinkForTask = (taskNum) => {
    const fromApi = getSocialUrlFromOtherSettings(otherSettings, taskNum);
    const href = fromApi || SOCIAL_LINK_FALLBACK[taskNum] || "#";
    return { href, label: SOCIAL_DEFAULT_LABELS[taskNum] || "Open link" };
  };

  const tasksLockedAfterClaim = Boolean(referralRewardStatus?.rewardClaimed);

  const handleCompleteTask = useCallback(
    async (taskNum) => {
      if (!isLoggedIn) return;
      if (tasksLockedAfterClaim) {
        return;
      }
      const n = Number(taskNum);
      if (!Number.isInteger(n) || n < 1 || n > 5) {
        alertErrorMessage("Invalid task. Use tasks 1 through 5.");
        return;
      }
      setTaskSubmitting(taskNum);
      try {
        const res = await AuthService.completeReferralSocialTask(n);
        if (res?.success) {
          alertSuccessMessage(res?.message || "Task recorded");
          if (typeof fetchReferralRewardStatus === "function") {
            setRewardStatusLoading(true);
            try {
              await fetchReferralRewardStatus();
            } finally {
              setRewardStatusLoading(false);
            }
          }
        } else {
          const msg = res?.message || "Could not update task";
          const maybeClaimed = /already\s+claimed|reward\s*claimed/i.test(msg);
          alertErrorMessage(parseReferralSocialTaskErrorMessage(msg));
          if (maybeClaimed && typeof fetchReferralRewardStatus === "function") {
            try {
              await fetchReferralRewardStatus();
            } catch {
              /* ignore */
            }
          }
        }
      } catch (err) {
        alertErrorMessage(parseReferralSocialTaskErrorMessage(err?.message));
      } finally {
        setTaskSubmitting(null);
      }
    },
    [isLoggedIn, fetchReferralRewardStatus, tasksLockedAfterClaim]
  );

  const canClaimSignupReward =
    Boolean(referralRewardStatus?.socialTasksCompleted) &&
    !referralRewardStatus?.rewardClaimed;

  const handleClaimSignupReward = useCallback(async () => {
    if (!isLoggedIn || !canClaimSignupReward || claimSubmitting) return;
    setClaimSubmitting(true);
    try {
      const res = await AuthService.claimSignupReward();
      if (res?.success) {
        alertSuccessMessage(res?.message || "Signup reward claimed");
        if (typeof fetchReferralRewardStatus === "function") {
          setRewardStatusLoading(true);
          try {
            await fetchReferralRewardStatus();
          } finally {
            setRewardStatusLoading(false);
          }
        }
      } else {
        alertErrorMessage(formatClaimSignupRewardError(res, socialTasksRequired, socialLinkTasksRequired));
        if (typeof fetchReferralRewardStatus === "function") {
          try {
            await fetchReferralRewardStatus();
          } catch {
            /* ignore */
          }
        }
      }
    } catch (err) {
      alertErrorMessage(formatClaimSignupRewardError(err, socialTasksRequired, socialLinkTasksRequired));
    } finally {
      setClaimSubmitting(false);
    }
  }, [
    isLoggedIn,
    canClaimSignupReward,
    claimSubmitting,
    fetchReferralRewardStatus,
    socialTasksRequired,
    socialLinkTasksRequired,
  ]);

  const scrollToAirdropTasksSection = useCallback(() => {
    document.getElementById(AIRDROP_TASKS_SECTION_HASH)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  useEffect(() => {
    const wantTasksAnchor = location.hash === `#${AIRDROP_TASKS_SECTION_HASH}`;
    if (wantTasksAnchor) {
      scrollToAirdropTasksSection();
      const t1 = window.setTimeout(scrollToAirdropTasksSection, 120);
      const t2 = window.setTimeout(scrollToAirdropTasksSection, 450);
      return () => {
        window.clearTimeout(t1);
        window.clearTimeout(t2);
      };
    }
    window.scrollTo(0, 0);
  }, [location.pathname, location.hash, scrollToAirdropTasksSection]);

  useEffect(() => {
    const sync = () => setIsLoggedIn(!!localStorage.getItem("token"));
    window.addEventListener("storage", sync);
    document.addEventListener("visibilitychange", sync);
    return () => {
      window.removeEventListener("storage", sync);
      document.removeEventListener("visibilitychange", sync);
    };
  }, []);

  useEffect(() => {
    if (!isLoggedIn || !fetchReferralRewardStatus) return;
    let cancel = false;
    (async () => {
      setRewardStatusLoading(true);
      try {
        await fetchReferralRewardStatus();
      } finally {
        if (!cancel) setRewardStatusLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [isLoggedIn, fetchReferralRewardStatus]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await AuthService.getOtherSettings();
      if (cancelled) return;
      if (res?.success && res?.data) {
        setOtherSettings(res.data);
      }
      setSettingsLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Zillion AirDrop – 1000 Token Rewards</title>
        <meta
          name="description"
          content="Join the Zillion AirDrop and earn token rewards. Sign up, refer friends, and claim rewards with simple steps."
        />
      </Helmet>

      <div
        className="airdrop_page"
        style={{
          "--airdrop-bonusbg": `url(${bonusBgUrl})`,
          "--airdrop-releasebg": `url(${releaseBgUrl})`,
          "--airdrop-releasebg-light": `url(${releaseBgLightUrl})`,
          "--airdrop-hero-light-bg": `url(${heroLightBgUrl})`,
        }}
      >
        <section
          className="airdrop_hero"
          style={{ backgroundImage: `url(${heroBgUrl})` }}
        >
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-6">
                <div className="airdrop_hero_content">
                  <h1>
                    1000 TOKEN <br /> AIRDROP
                  </h1>
                  <p>
                    Join early. Earn more. Be part of the future.
                  </p>

                  <p>
                    Complete simple tasks, follow our social channels, and earn up to 1000 tokens in our
                    exclusive airdrop campaign.
                  </p>
                  <div className="airdrop_hero_actions">
                    <button type="button" className="airdrop_btn_primary" onClick={scrollToAirdropTasksSection}>
                      Join Airdrop Now
                    </button>
                    <button type="button" className="airdrop_btn_secondary" onClick={scrollToAirdropTasksSection}>
                      How It Works
                    </button>
                  </div>

                  <div className="joined_user">
                    <img src="/images/airdrop-img/abcd_vector.svg" alt="joined user" /><span><b>150+</b> people joined</span>
                  </div>

                  {/* <div className="airdrop_social_row" aria-label="social links">
                    <a href="#" className="airdrop_social" aria-label="Twitter">
                      <i className="ri-twitter-x-line" />
                    </a>
                    <a href="#" className="airdrop_social" aria-label="Telegram">
                      <i className="ri-telegram-2-fill" />
                    </a>
                    <a href="#" className="airdrop_social" aria-label="Instagram">
                      <i className="ri-instagram-line" />
                    </a>
                    <a href="#" className="airdrop_social" aria-label="YouTube">
                      <i className="ri-youtube-line" />
                    </a>
                  </div> */}
                </div>
              </div>

              <div className="col-lg-6">
                <div className="airdrop_hero_visual">
                  <div className="airdrop_visual_glow" />
                  <img
                    className="airdrop_visual_img"
                    src="/images/airdrop-img/airdrop_bnr_img.png"
                    alt="Zillion rewards"
                  />

                  <img className="airdrop_back_img dark_img" src="/images/airdrop-img/airdrop_back_img.png" alt="Zillion rewards" />
                  <img className="airdrop_back_img light_img" src="/images/airdrop-img/airdrop_back_light_img.png" alt="Zillion rewards" />

                </div>

              </div>
            </div>

           
          </div>
        </section>

 
          <div className="container">
          <div className="airdrop_stats">
              <div className="airdrop_stat_card">
                <div className="airdrop_stat_icon airdrop_stat_icon--purple">
                 <img src="/images/airdrop-img/airdrop_stats_icon.png" alt="Zillion rewards" />
                </div>
                <div className="airdrop_stat_text">
                  <h4>{!settingsLoaded ? "…" : signupDisplay}</h4>
                  <p>Signup Bonus</p>
                </div>
              </div>
              <div className="airdrop_stat_card airdrop_stat_card_blue">
                <div className="airdrop_stat_icon airdrop_stat_icon--blue">
                <img src="/images/airdrop-img/airdrop_stats_icon2.png" alt="Zillion rewards" />
                </div>
                <div className="airdrop_stat_text">
                  <h4>50K+</h4>
                  <p>Participants</p>
                </div>
              </div>
              <div className="airdrop_stat_card airdrop_stat_card_green">
                <div className="airdrop_stat_icon airdrop_stat_icon--green">
                <img src="/images/airdrop-img/airdrop_stats_icon3.png" alt="Zillion rewards" />
                </div>
                <div className="airdrop_stat_text">
                  <h4>Verified</h4>
                  <p>Task System</p>
                </div>
              </div>
              <div className="airdrop_stat_card airdrop_stat_card_orange">
                <div className="airdrop_stat_icon airdrop_stat_icon--orange">
                <img src="/images/airdrop-img/airdrop_stats_icon4.png" alt="Zillion rewards" />
                </div>
                <div className="airdrop_stat_text">
                  <h4>Free</h4>
                  <p>Join in seconds</p>
                </div>
              </div>
            </div>
          </div>
 

        <section className="airdrop_steps" id={AIRDROP_TASKS_SECTION_HASH}>
          <div className="container">
            <h2>How To Join In <span>{GUEST_STEPS.length} Easy Steps!</span></h2>

            {!isLoggedIn && (
            <div className="row g-4 mt-2">
              {GUEST_STEPS.map((s) => (
                <div className="col-md-6 col-xl-4" key={s.n}>
                  <div className="airdrop_step_card">
                    <div className={`airdrop_step_num ${stepNumClass[(s.n - 1) % stepNumClass.length]}`}>{s.n}</div>
                    <h3>{s.title}</h3>
                    <p>{s.body}</p>
                  </div>
                </div>
              ))}
            </div>
            )}

            {isLoggedIn && (
            <>
            <p className="airdrop_steps_logged_sub">
              Finish {socialProgress.total} social link tasks below ({socialProgress.done}/{socialProgress.total} completed).
              Open each channel link once so progress syncs to your account. KYC is not required for this referral signup reward.
            </p>
            {referralRewardStatus?.rewardClaimed ? (
              <p className="airdrop_onboarding_load mt-2" aria-live="polite">
                You have already claimed this referral signup reward. Social task steps are shown as completed.
              </p>
            ) : null}
            {(rewardStatusLoading && !referralRewardStatus) && (
              <p className="airdrop_onboarding_load mt-2" aria-live="polite">Loading your tasks…</p>
            )}
            {referralRewardStatus != null || !rewardStatusLoading ? (
              <>
              <div className="airdrop_task_progress" aria-label="Airdrop social task progress">
                <div className="airdrop_task_progress_top">
                  <span className="airdrop_task_progress_label">Task progress</span>
                  <span className="airdrop_task_progress_count">
                    {socialProgress.done} / {socialProgress.total} completed
                    {rewardStatusLoading && " (updating…)"}
                  </span>
                </div>
                <div
                  className="airdrop_task_progress_track"
                  role="progressbar"
                  aria-valuenow={socialProgress.done}
                  aria-valuemin={0}
                  aria-valuemax={socialProgress.total}
                >
                  <div className="airdrop_task_progress_fill" style={{ width: `${socialProgress.pct}%` }} />
                </div>
                <div className="airdrop_task_progress_steps">
                  {SOCIAL_TASK_DEFS.map(({ n: stepN, progressLabel, iconClass }) => (
                    <div
                      key={stepN}
                      className={`airdrop_task_progress_step ${isTaskComplete(stepN) ? "is-done" : "is-todo"}`}
                    >
                      <span className="airdrop_task_progress_icon_circle" aria-hidden>
                        <i className={iconClass} />
                      </span>
                      <span className="airdrop_task_progress_stepname">{progressLabel}</span>
                    </div>
                  ))}
                  {/* <div
                    className={`airdrop_task_progress_step ${isTaskComplete(mobileTaskId) ? "is-done" : "is-todo"}`}
                  >
                    <span className="airdrop_task_progress_icon_circle" aria-hidden>
                      <i className={MOBILE_TASK_PROGRESS.iconClass} />
                    </span>
                    <span className="airdrop_task_progress_stepname">{MOBILE_TASK_PROGRESS.progressLabel}</span>
                  </div> */}
                </div>
              </div>
              {canClaimSignupReward ? (
                <div className="airdrop_claim_inline mt-3 d-flex flex-column align-items-center justify-content-center gap-2 text-center">
                  <button
                    type="button"
                    className="airdrop_btn_primary"
                    disabled={claimSubmitting || rewardStatusLoading}
                    onClick={handleClaimSignupReward}
                  >
                    {claimSubmitting ? "Claiming…" : "Claim signup reward"}
                  </button>
                  <span className="text-muted small">
                    All {socialProgress.total} steps completed — claim your referral signup bonus.
                  </span>
                </div>
              ) : null}
              </>
            ) : null}

            <div className="row g-4 mt-2">
              {SOCIAL_TASK_DEFS.map((s) => {
                const n = s.n;
                const link = getLinkForTask(n);
                const numClass = stepNumClass[(n - 1) % stepNumClass.length];
                const done = isTaskComplete(n);
                return (
                  <div className="col-md-4" key={n}>
                    <div className={`airdrop_step_card airdrop_task_card ${done ? "airdrop_task_card--done" : ""}`}>
                      <div className={`airdrop_step_num airdrop_task_social_badge ${numClass}`}>
                        <i className={s.iconClass} aria-hidden />
                      </div>
                      <h3>{s.title}</h3>
                      {done && <p className="airdrop_task_done_badge">Completed</p>}
                      <p className="airdrop_task_hint">{s.hint}</p>
                      <div className="airdrop_task_actions">
                        <a
                          className="airdrop_task_link_out"
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-busy={taskSubmitting === n}
                          onClick={() => {
                            const canSubmitNow =
                              !done && !tasksLockedAfterClaim && !rewardStatusLoading && !taskSubmitting;
                            if (canSubmitNow) handleCompleteTask(n);
                          }}
                        >
                          {taskSubmitting === n ? "Saving…" : link.label}
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
              {/* <div className="col-md-4">
                <div
                  className={`airdrop_step_card airdrop_task_card ${
                    isTaskComplete(mobileTaskId) ? "airdrop_task_card--done" : ""
                  }`}
                >
                  <div
                    className={`airdrop_step_num airdrop_task_social_badge ${
                      stepNumClass[2]
                    }`}
                  >
                    <i className={MOBILE_TASK_PROGRESS.iconClass} aria-hidden />
                  </div>
                  <h3>Verified mobile</h3>
                  {isTaskComplete(mobileTaskId) ? <p className="airdrop_task_done_badge">Completed</p> : null}
                  {isPhoneSignupUser ? (
                    <p className="airdrop_task_hint">
                      You registered with a phone number — this step is satisfied automatically when your account shows a
                      verified mobile (confirm above via task progress).
                    </p>
                  ) : (
                    <>
                      <p className="airdrop_task_hint">
                        Verify Your Mobile Number to 
                        claim your signup bonus.
                      </p>
                      {!isTaskComplete(mobileTaskId) ? (
                        <div className="airdrop_task_actions">
                          <Link
                            className="airdrop_task_link_out"
                            to="/user_profile/two_factor_autentication"
                          >
                            Add &amp; verify mobile
                          </Link>
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              </div> */}
            </div>
            </>
            )}
          </div>
        </section>

        <section className="airdrop_rewards">
          <div className="container">
            <div className="airdrop_section_heading">
              <h2>Token &amp; Rewards</h2>
              <p>Your rewards unlocked over time.</p>
            </div>

            <div className="row g-4 mt-2 token_rewards_sl">
              <div className="col-lg-6">
                <div className="airdrop_reward_card">
                  <div className="airdrop_reward_top">
                    <h3>Signup Bonus</h3>
                    <div className="airdrop_reward_value">{!settingsLoaded ? "…" : signupDisplay}</div>
                  </div>
                  <p className="airdrop_reward_sub">
                    Complete signup and {socialProgress.total} channel link visits to unlock your signup bonus — KYC not required for this reward.
                  </p>
                  <div className="airdrop_reward_meta">
                   <div className="airdrop_reward_meta_item"> <span><img src="/images/airdrop-img/check.svg" alt="Zillion rewards" /> Level 1</span><b></b></div>
                    <div className="airdrop_reward_meta_item"> <span><img src="/images/airdrop-img/check.svg" alt="Zillion rewards" />Level 2</span><b></b></div>
                    <div className="airdrop_reward_meta_item"> <span><img src="/images/airdrop-img/check.svg" alt="Zillion rewards" />Level 3</span><b></b></div>
                  </div>
                </div>
              </div>

              <div className="col-lg-6">
                <div className="airdrop_reward_card bonus_unlimited">
                  <div className="airdrop_reward_top">
                    <h3>Referral Bonus (Unlimited)</h3>
                    <div className="airdrop_reward_value">{!settingsLoaded ? "…" : referralTotalDisplay}</div>
                  </div>
                  <p className="airdrop_reward_sub">
                    Earn token rewards for each friend you refer — scale your earnings with your network.
                  </p>


                  <div className="airdrop_reward_meta">
                   <div className="airdrop_reward_meta_item"> <span><img src="/images/airdrop-img/check.svg" alt="Zillion rewards" /> Level 1</span><b>{!settingsLoaded ? "…" : level1Display}</b></div>
                    <div className="airdrop_reward_meta_item"> <span><img src="/images/airdrop-img/check.svg" alt="Zillion rewards" />Level 2</span><b>{!settingsLoaded ? "…" : level2Display}</b></div>
                    <div className="airdrop_reward_meta_item"> <span><img src="/images/airdrop-img/check.svg" alt="Zillion rewards" />Level 3</span><b>{!settingsLoaded ? "…" : level3Display}</b></div>
                  </div>


                  {/* <div className="airdrop_reward_rows">
                    <div className="airdrop_reward_row">
                      <span>Level 1</span>
                      <b>100T</b>
                    </div>
                    <div className="airdrop_reward_row">
                      <span>Level 2</span>
                      <b>100T</b>
                    </div>
                    <div className="airdrop_reward_row">
                      <span>Level 3</span>
                      <b>50T</b>
                    </div>
                  </div> */}



                </div>
              </div>
            </div>

            {/* <div className="airdrop_cta_row">
              <NavLink className="airdrop_cta_link" to="/refer_earn">
                Explore Referral Program <i className="ri-arrow-right-line" />
              </NavLink>
            </div> */}
          </div>
        </section>

        <section className="airdrop_release">
          <div className="container">
            <h2>Best of <span>Release Plan</span></h2>

            <div className="row g-4 mt-2">
              {[
                { title: "Round 1", pct: "10%", sub: "Token Distribution", time: "1 - 3 Months" },
                { title: "Round 2", pct: "15%", sub: "Token Distribution", time: "4 - 6 Months" },
                { title: "Round 3", pct: "25%", sub: "Token Distribution", time: "7 - 12 Months" },
                { title: "Round 4", pct: "50%", sub: "Token Distribution", time: "13 - 24 Months" },
              ].map((x) => (
                <div className="col-md-6 col-lg-3" key={x.title}>
                  <div className="airdrop_release_card">
                    <div className="airdrop_release_badge">{x.title}</div>
                    <div className="airdrop_release_icon">
                      <img src="/images/airdrop-img/tokenlock.svg" alt="Zillion rewards" />
                    </div>
                    <h6>AVAILABLE REWARDS:</h6>
                    <div className="airdrop_release_pct">{x.pct}</div>
                    <div className="airdrop_release_sub">{x.sub}</div>
                    <div className="airdrop_release_time">{x.time}</div>
                    <div className="airdropsubtext">Early bird special allocation</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AirDrop;

